export const INSIGHTS = [
  {
    slug: "practical-technology-roadmap",
    title: "How to Create a Practical Technology Roadmap",
    category: "Technology Strategy",
    readTime: "6 min read",
    date: "June 2026",
    excerpt:
      "A roadmap turns scattered technology priorities into a clear, sequenced plan. Here's how to build one that leadership and IT can actually rally behind.",
    body: [
      {
        heading: "Start with business goals, not tools",
        paragraphs: [
          "A technology roadmap is not a shopping list of software. It's a plan that connects what the business is trying to achieve over the next one to three years with the technology decisions that support those goals.",
          "Before evaluating any tool, get clear on the priorities: where is the business heading, what's slowing it down, and what outcomes matter most to leadership? Every roadmap item should trace back to one of those answers.",
        ],
      },
      {
        heading: "Inventory and honestly assess what you have",
        paragraphs: [
          "Document your current systems, what they cost, and how well they serve the business today. Look for overlap, underused tools, aging platforms, and gaps where manual work fills in for missing capability.",
          "This honest current-state view is often uncomfortable — but it's the foundation for every good decision that follows.",
        ],
      },
      {
        heading: "Prioritize by value and dependency",
        paragraphs: [
          "Not everything can happen at once. Rank initiatives by the business value they create and the dependencies between them. Some projects unlock others; some can wait without consequence.",
          "A simple value-versus-effort view is usually enough to surface the handful of priorities that deserve attention first.",
        ],
      },
      {
        heading: "Sequence, budget, and keep it living",
        paragraphs: [
          "Lay the priorities out on a timeline tied to budget, so leadership can see what's coming and plan for it. Then treat the roadmap as a living document — review it on a regular cadence and adjust as the business changes.",
          "The goal isn't a perfect prediction of the future. It's a shared, deliberate plan that replaces reactive, crisis-driven decisions.",
        ],
      },
    ],
    relatedService: "it-strategy-roadmapping",
  },
  {
    slug: "evaluate-legacy-software",
    title: "How to Evaluate Legacy Software Before Replacing It",
    category: "Modernization",
    readTime: "5 min read",
    date: "June 2026",
    excerpt:
      "Replacing a legacy system is expensive and disruptive. Before you commit, run this honest evaluation of fit, risk, and the real cost of staying put.",
    body: [
      {
        heading: "Don't replace on instinct",
        paragraphs: [
          "Legacy systems get a bad reputation, but 'old' isn't the same as 'wrong.' Some aging systems still serve the business well; others quietly constrain growth, security, and reporting. The job is to tell the difference deliberately.",
        ],
      },
      {
        heading: "Assess business fit and risk",
        paragraphs: [
          "Start with how well the system supports the way the business operates today and where it falls short. Then weigh the risks of keeping it: security exposure, vendor support, integration limits, and the difficulty of finding people who can maintain it.",
          "A system that's hard to secure or impossible to extend carries a cost even if it still 'works.'",
        ],
      },
      {
        heading: "Compare the cost of staying versus moving",
        paragraphs: [
          "Modernization has obvious costs, but staying put has hidden ones — manual workarounds, missed reporting, lost flexibility, and rising risk. Put both sides on the table honestly before deciding.",
        ],
      },
      {
        heading: "Decide, then move in phases",
        paragraphs: [
          "If modernization is the right call, resist the urge to replace everything at once. A phased plan that tackles the highest-value, highest-risk areas first delivers value sooner and keeps disruption manageable.",
        ],
      },
    ],
    relatedService: "software-modernization",
  },
  {
    slug: "do-you-need-a-fractional-cto",
    title: "How to Decide Whether Your Business Needs a Fractional CTO",
    category: "Executive Leadership",
    readTime: "5 min read",
    date: "June 2026",
    excerpt:
      "Many growing businesses need executive technology judgment but can't justify a full-time CTO. Here's how to know if a fractional advisor is the right fit.",
    body: [
      {
        heading: "The leadership gap",
        paragraphs: [
          "As businesses grow, technology decisions get bigger and more consequential — but a full-time CTO is a significant investment that many organizations can't yet justify. That gap leaves leadership making high-stakes calls without a trusted technical voice.",
        ],
      },
      {
        heading: "Signs a fractional CTO may help",
        paragraphs: [
          "Consider fractional leadership if technology decisions keep landing on people without the time or background to make them, if your internal IT lacks strategic direction, or if you're facing major initiatives — a system replacement, a security concern, an acquisition — without senior guidance.",
        ],
      },
      {
        heading: "What a fractional CTO actually does",
        paragraphs: [
          "A fractional CTO brings executive-level judgment on a part-time basis: setting technology direction, overseeing key initiatives and vendors, mentoring internal staff, and translating technology decisions into business terms for leadership.",
        ],
      },
      {
        heading: "When a full-time hire makes more sense",
        paragraphs: [
          "If technology is core to your product, your team is large enough to need daily leadership, or you have a constant pipeline of complex initiatives, a full-time CTO may be the right move. A fractional engagement can also be a sensible bridge until that point.",
        ],
      },
    ],
    relatedService: "fractional-cto",
  },
  {
    slug: "systems-that-dont-communicate",
    title: "What to Do When Your Business Systems Don't Communicate",
    category: "Systems Integration",
    readTime: "5 min read",
    date: "June 2026",
    excerpt:
      "Disconnected systems create manual work, data errors, and reporting delays. Here's how to diagnose the problem and fix it deliberately.",
    body: [
      {
        heading: "The quiet cost of disconnected systems",
        paragraphs: [
          "When your tools don't share data, staff re-enter the same information in multiple places, mistakes multiply, and reporting becomes slow and unreliable. Over time, this manual glue quietly caps how much the business can grow.",
        ],
      },
      {
        heading: "Map how data should flow",
        paragraphs: [
          "Before connecting anything, get clear on how information should move across the business — from sales to operations to finance. A simple map of where data originates and where it needs to go reveals the connections that matter most.",
        ],
      },
      {
        heading: "Choose the right integration approach",
        paragraphs: [
          "Not every system needs a deep, real-time integration. Some need a reliable nightly sync; others a purpose-built connection. The right approach balances business value, reliability, and what's maintainable over time — avoiding the fragile, ad-hoc links that break.",
        ],
      },
      {
        heading: "Prioritize and start small",
        paragraphs: [
          "Tackle the highest-value connection first — usually the one removing the most manual work or fixing the most painful reporting gap. Early wins build momentum and prove the approach before you scale it across the business.",
        ],
      },
    ],
    relatedService: "systems-integration",
  },
];

export const getArticle = (slug) => INSIGHTS.find((a) => a.slug === slug);

// ---------- Pricing / Engagement Models ----------
